import { useState } from 'react';
import { MARKET_PROMOTION_PERMISSIONS } from '@/constants/permissions';
import { useQuery } from '@tanstack/react-query';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { ExportButton } from '@/components/common/ExportButton';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { TAKA_SYMBOL } from '@/constants/app.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { formatCount } from '@/utils/number';
import { mpApprovalsInfiniteQueryOptions } from '../api/marketPromotion.api';
import { approvalTotalsQueryOptions, exportCashCollection } from '../api/marketPromotionP2.api';
import { DonationApprovalFilterPanel } from '../components/DonationApprovalFilterPanel';
import { APPROVAL_ZONAL_MANAGER_COLUMN_ID, getMpApprovalColumns } from '../components/MpColumns';
import {
  DONATION_DETAIL_ORIGIN,
  DONATION_SEARCH_PLACEHOLDER,
} from '../constants/marketPromotion.constants';
import {
  useApprovalReportFilters,
  type ApprovalReportRouteId,
} from '../hooks/useApprovalReportFilters';
import { useOpenDonationDetail } from '../hooks/useOpenDonationDetail';
import { canDecideFrom } from '../utils/donationDecision';

const EXPORT_FILE_NAME_PREFIX: Record<ApprovalReportRouteId, string> = {
  approvalReport: 'approval-report',
  cashCollection: 'cash-collection',
};

interface ApprovalReportLayoutProps {
  title: string;
  routeId: ApprovalReportRouteId;
}

export function ApprovalReportLayout({ title, routeId }: ApprovalReportLayoutProps) {
  const controller = useApprovalReportFilters(routeId);
  const { search } = controller;

  const { rows, isLoading, loadError, refetch, loadMoreProps } = useInfiniteTableQuery(
    mpApprovalsInfiniteQueryOptions(search, false),
  );
  const { data: totals, isLoading: isTotalsLoading } = useQuery(approvalTotalsQueryOptions(search));

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const from = DONATION_DETAIL_ORIGIN[routeId];
  const { openDonationDetail, decisionActions, isDonationHighlighted } =
    useOpenDonationDetail(from);
  const columns = getMpApprovalColumns(
    {
      ...(canDecideFrom(from) ? decisionActions : {}),
      onView: openDonationDetail,
    },
    false,
    search.search,
  );

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title={title}
        titleAddon={
          <DateRangeBadge
            onClick={() => setIsFilterOpen(true)}
            dateFrom={search.dateFrom}
            dateTo={search.dateTo}
          />
        }
        search={{
          value: controller.searchText,
          onChange: controller.setSearchText,
          placeholder: DONATION_SEARCH_PLACEHOLDER,
        }}
        actions={
          <>
            <AdvancedFilterButton
              isOpen={isFilterOpen}
              activeCount={controller.activeFilterCount}
              onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
            />
            <ExportButton
              requires={MARKET_PROMOTION_PERMISSIONS.CASH_COLLECTION_EXPORT}
              onExport={() => exportCashCollection(search, EXPORT_FILE_NAME_PREFIX[routeId])}
              disabled={isLoading || loadError !== null}
            />
          </>
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <DonationApprovalFilterPanel filters={controller} />
      </FilterPanelCollapse>

      <StatCardGrid
        isLoading={isTotalsLoading}
        stats={(totals ?? []).map((row) => ({
          key: row.paymentType,
          label: `${row.paymentType} · ${formatCount(row.donations, 'donation')}`,
          value: Math.round(row.approved),
          unit: TAKA_SYMBOL,
        }))}
      />

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the report"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId={APPROVAL_ZONAL_MANAGER_COLUMN_ID}
          isRowHighlighted={isDonationHighlighted}
          emptyMessage="No donation matches"
          {...loadMoreProps}
        />
      )}
    </div>
  );
}
